import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import RightPanel from "../../../../common/rightPanel/RightPanel";
import "./styles/index.scss";
import CheckIcon from "../../../../common/icons/checkIcon";
import Button from "../../../../common/forms/button";
import CheckBoxIcon from "../../../../common/icons/checkboxes/checkboxIcon";
import * as api from "../../../../../api/generated-api";
import { Box, MenuItem, Typography, useTheme } from "@mui/material";
import { DKDialog } from "../../../../common/DKDialog";
import { ProgressIndicator } from "../../../../common/progress/ProgressIndicator";
import { getDealSignaturePacketDownloadPDFForUser } from "../../../../../api/deals/signatures";
import { DealDocumentRowItemActionsMenu } from "../../../dealDocuments/DealDocumentsPage/DealDocumentRowItemActionsMenu";
import { StyledListItemIcon } from "../../../dealDocuments/DealDocumentsPage/DealDocumentRowItem";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { getDealLegalEntity } from "../../../../../api/deals/deallegalentities";

type SignaturesPanelPropTypes = {
  orgId: number;
  dealId: number;
  label: string;
  canEdit: boolean;
  signatures: api.DealDocumentSignature[];
  documents: api.DealDocument[];
  onRequest: (
    signaturesToBeRequested: api.DealDocumentSignature[],
    isReminder?: boolean
  ) => void;
  onClose: () => void;
};

const SignaturesRequestPanel = ({
  orgId,
  dealId,
  label,
  canEdit,
  signatures,
  documents,
  onRequest,
  onClose,
}: SignaturesPanelPropTypes) => {
  const [signaturesToBeRequested, setSignaturesToBeRequested] = useState<
    api.DealDocumentSignature[]
  >([]);
  const [
    showDownloadSignaturePacketDialog,
    setShowDownloadSignaturePacketDialog,
  ] = useState<boolean>(false);
  const [isDownloadingSignaturePacket, setIsDownloadingSignaturePacket] =
    useState<boolean>(false);
  const [personForSignature, setPersonForSignature] =
    useState<api.Person>(null);

  const theme = useTheme();

  const receivedList = signatures.filter(
    (s) => s.status === api.SigningStatus.signed
  );
  const requestedList = signatures.filter(
    (s) => s.status === api.SigningStatus.requested
  );

  const notRequestedList = signatures.filter(
    (s) => s.status === api.SigningStatus.notRequested
  );

  const handleSendOrRemindSignatureRequest = (
    requestedList: api.DealDocumentSignature[],
    isReminder: boolean
  ) => {
    onRequest(requestedList, isReminder);
    setSignaturesToBeRequested([]);
  };

  useEffect(() => {
    getDealLegalEntity(
      orgId,
      dealId,
      signatures[0].dealLegalEntity.id,
      (dealLegalEntity) => {
        setPersonForSignature(dealLegalEntity.person);
      }
    );
  }, [signatures]);

  const handleDownloadSignaturePacket = () => {
    // show activity indicator
    setIsDownloadingSignaturePacket(true);

    // download signature packet and wait
    getDealSignaturePacketDownloadPDFForUser(
      orgId,
      dealId,
      signatures[0].dealLegalEntity.personId,
      () => {
        // when download is complete, hide activity indicator
        setShowDownloadSignaturePacketDialog(false);

        setTimeout(() => {
          // and reset is downloading state after 1 second to allow the dialog to close to prevent download button flickering
          setIsDownloadingSignaturePacket(false);
        }, 1 * 1000);
      }
    );
  };

  const personStringForSignaturePacketDialog = personForSignature
    ? `${personForSignature.firstName} ${personForSignature.lastName}`
    : "requested person";

  return (
    <Box className="signatures-panel-container">
      {/* marginTop is a workaround until the Signatures and Wire Tracking page is refactored to use DealsPageLayout */}
      <RightPanel title="Signatures" onClose={onClose} marginTop="2px">
        <Box>
          <Box className="signatures-panel">
            <Box display={"flex"} flexDirection={"row"}>
              <Box flex={1}>
                <Box className="signatures-for-label">Signatures for</Box>
                <Box className="signatures-for-text">{label}</Box>
              </Box>
              <Box width={"50px"} justifyContent={"flex-end"}>
                <DealDocumentRowItemActionsMenu
                  dataTestId={`signatures-panel-overflow-menu`}
                >
                  <MenuItem
                    onClick={() => {
                      setShowDownloadSignaturePacketDialog(true);
                    }}
                  >
                    <StyledListItemIcon>
                      <FileDownloadOutlinedIcon
                        style={{ color: theme.palette.secondary.darker }}
                      />
                    </StyledListItemIcon>
                    <Typography
                      data-testid={`download-signature-packet-menu-item`}
                      sx={{
                        color: theme.palette.secondary.dark,
                      }}
                    >
                      Download Signature Packet
                    </Typography>
                  </MenuItem>
                </DealDocumentRowItemActionsMenu>
              </Box>
            </Box>
            {signatures && !!receivedList.length && (
              <Box className="signatures-panel-label">Received signatures</Box>
            )}
            {signatures &&
              !!receivedList.length &&
              receivedList.map((s, key) => {
                //display documents that have been signed
                return (
                  <Box
                    key={"received_" + key}
                    className="received-signature-container"
                  >
                    <CheckIcon signingStatus={api.SigningStatus.signed} />
                    <Box className="document-name">
                      {documents.find((d) => d.id === s.dealDocumentId)?.name}
                    </Box>
                  </Box>
                );
              })}
            {signatures && !!requestedList.length && (
              //display if signatures have been requested
              <Box className="signatures-panel-label">
                Signature Requested
                <Box className="line" />
                {canEdit && (
                  <Button
                    type="secondary"
                    size="small"
                    onClick={() => {
                      handleSendOrRemindSignatureRequest(requestedList, true);
                    }}
                  >
                    send reminder
                  </Button>
                )}
              </Box>
            )}
            {signatures &&
              !!requestedList.length &&
              requestedList.map((s, key) => {
                //display documents for which signatures have been requested
                return (
                  <Box
                    data-testid="document-requested-signature"
                    key={"requested_" + key}
                    className="requested-signature-container"
                  >
                    <CheckIcon signingStatus={api.SigningStatus.requested} />
                    <Box className="document-name">
                      {documents.find((d) => d.id === s.dealDocumentId)?.name}
                    </Box>
                  </Box>
                );
              })}
            {signatures && !!notRequestedList.length && (
              //display if signatures have not been requested
              <Box className="signatures-panel-label">
                Not yet Requested
                <Box className="line" />
                {canEdit && (
                  <Button
                    data-testid="button-request-signatures"
                    type={
                      (!!signaturesToBeRequested.length && "secondary") ||
                      "disabled-secondary"
                    }
                    size="small"
                    onClick={() => {
                      handleSendOrRemindSignatureRequest(
                        signaturesToBeRequested,
                        false
                      );
                    }}
                  >
                    request signatures
                  </Button>
                )}
              </Box>
            )}
            {signatures &&
              !!notRequestedList.length &&
              notRequestedList.map((s, key) => {
                //display documents for which signatures have not been requested
                const isChecked = !!signaturesToBeRequested.find(
                  (r) => r.id === s.id
                );
                if (canEdit) {
                  return (
                    <Box
                      data-testid="document-not-requested-signature"
                      data-test-checked={isChecked}
                      key={"not-requested_" + key}
                      className="not-requested-signature-container"
                      onClick={() => {
                        if (
                          !signaturesToBeRequested.find((r) => r.id === s.id)
                        ) {
                          //add signature if checked
                          setSignaturesToBeRequested([
                            ...signaturesToBeRequested,
                            s,
                          ]);
                        } else {
                          //remove signature if unchecked
                          setSignaturesToBeRequested(
                            signaturesToBeRequested.filter((r) => r.id !== s.id)
                          );
                        }
                      }}
                    >
                      <CheckBoxIcon isChecked={isChecked} />
                      <Box className="document-name">
                        {documents.find((d) => d.id === s.dealDocumentId)?.name}
                      </Box>
                    </Box>
                  );
                } else {
                  return (
                    <Box
                      data-testid="document-not-requested-signature"
                      data-test-checked={isChecked}
                      key={"not-requested_" + key}
                      className="not-requested-signature-container"
                    >
                      <CheckIcon
                        signingStatus={api.SigningStatus.notRequested}
                      />
                      <Box className="document-name">
                        {documents.find((d) => d.id === s.dealDocumentId)?.name}
                      </Box>
                    </Box>
                  );
                }
              })}
          </Box>
          <DKDialog
            title="Download Signature Packet"
            open={showDownloadSignaturePacketDialog}
            actionButtons={
              isDownloadingSignaturePacket
                ? []
                : [
                    {
                      dataTestId: "cancel-signature-packet-button",

                      label: "Cancel",
                      onClick: () => {
                        setShowDownloadSignaturePacketDialog(false);
                      },
                    },
                    {
                      dataTestId: "download-signature-packet-button",
                      primary: true,
                      label: "Generate Signature Packet",
                      onClick: handleDownloadSignaturePacket,
                    },
                  ]
            }
          >
            <Typography mt={2}>
              {`You can download a PDF signature packet for `}
              <strong>{personStringForSignaturePacketDialog}</strong>
              {` suitable for signing outside of Dealkit. The packet will take a few seconds to generate and then automatically download.`}
            </Typography>
            <Typography
              mt={2}
              data-testid="download-signature-packet-dialog-text"
            >
              Email the signed signature packet to{" "}
              <a href="mailto:support@dealkit.com?subject=Signed%20Signature%20Packet">
                support@dealkit.com
              </a>{" "}
              and the signatures will be processed within 1 business day.
            </Typography>
            {isDownloadingSignaturePacket && (
              <Box
                display={"flex"}
                justifyContent={"flex-end"}
                alignItems={"center"}
                mt={2}
              >
                <Box>
                  <ProgressIndicator size={30} />
                </Box>
                <Typography ml={2}>Generating...</Typography>
              </Box>
            )}
          </DKDialog>
        </Box>
      </RightPanel>
    </Box>
  );
};

const mapDispatchToProps = () => ({});

export default connect(null, mapDispatchToProps)(SignaturesRequestPanel);
